import { useEffect, useState } from 'react';
import { getFormations } from '../services/formationService';
import {
  getDevisById,
  telechargerPdfDevis,
  regenererPdfDevis,
  updateDevis,
  validerDevis,
  envoyerDevis,
  apercuEnvoiDevis,
} from '../services/devisService';
import ApercuEmail from './ApercuEmail';
import {
  formatAnciennete,
  formatDate,
  formatDateTime,
  formatMontant,
  STATUT_DEVIS_LABELS,
} from './devisUtils';
import SelectRecherche from '../components/SelectRecherche';
import SelectVille from '../components/SelectVille';
import RemplirParTexte from '../components/RemplirParTexte';
import {
  IconeBatiment,
  IconeCalendrier,
  IconeCoche,
  IconeCorbeille,
  IconeGroupe,
  IconeListe,
  IconePlus,
  IconeTexte,
} from './BoutonIcone';
import SelectSessionCalendrier, {
  estInter,
  libelleSession,
  useSessionsCalendrier,
} from '../components/SelectSessionCalendrier';
import '../assets/styles/DevisPanel.css';
import './Signature.css';
import { FlashToast } from '../components/Toasts';

const FORMATS = [
  { value: 'intra_presentiel', label: 'Intra présentiel' },
  { value: 'intra_distanciel', label: 'Intra distanciel' },
  { value: 'intra_mixte', label: 'Intra mixte' },
  { value: 'intra_presentiel_ou_distanciel', label: 'Intra présentiel ou distanciel' },
  { value: 'inter_presentiel', label: 'Inter présentiel' },
  { value: 'inter_distanciel', label: 'Inter distanciel' },
];

const FORMATS_SOUHAITES = [...FORMATS, { value: 'mixte', label: 'Mixte (INTRA / INTER)' }];

/**
 * Chemins renvoyés par l'IA dans champs_manquants -> champ du formulaire.
 * Sert à surligner ce qui reste à compléter avant validation.
 */
const CHEMIN_VERS_CHAMP = {
  'client.siret': 'siret',
  'client.adresse': 'adresse',
  'client.code_postal': 'codePostal',
  'client.ville': 'ville',
  'client.activite': 'activite',
  'client.convention_collective': 'conventionCollective',
  'cse.titulaires': 'cseTitulaires',
  'cse.suppleants': 'cseSuppleants',
  'cse.nb_premier_mandat': 'cseNbPremierMandat',
  'cse.nb_renouvellement': 'cseNbRenouvellement',
  'cse.nb_designes': 'cseNbDesignes',
  'cse.dernieres_elections': 'cseDernieresElections',
  'cse.contexte': 'cseContexte',
  'modalites.lieu': 'modalitesLieu',
  'modalites.periode_texte': 'modalitesPeriodeTexte',
  'modalites.format_souhaite': 'modalitesFormatSouhaite',
  'modalites.contraintes': 'modalitesContraintes',
};

/** <input type="date"> veut AAAA-MM-JJ ; l'API renvoie un ISO complet. */
function versDateInput(valeur) {
  if (!valeur) return '';
  return String(valeur).slice(0, 10);
}

function etatDepuisDevis(devis) {
  return {
    // Coordonnées client (stockées sur contacts)
    siret: devis.siret ?? '',
    adresse: devis.adresse ?? '',
    codePostal: devis.code_postal ?? '',
    ville: devis.ville ?? '',
    activite: devis.activite ?? '',
    conventionCollective: devis.convention_collective ?? '',
    // Composition du CSE
    cseEffectifEntreprise: devis.cse_effectif_entreprise ?? '',
    cseNbElus: devis.cse_nb_elus ?? '',
    cseTitulaires: devis.cse_titulaires ?? '',
    cseSuppleants: devis.cse_suppleants ?? '',
    cseNbPremierMandat: devis.cse_nb_premier_mandat ?? '',
    cseNbRenouvellement: devis.cse_nb_renouvellement ?? '',
    cseNbDesignes: devis.cse_nb_designes ?? '',
    cseDernieresElections: versDateInput(devis.cse_dernieres_elections),
    cseContexte: devis.cse_contexte ?? '',
    // Modalités
    modalitesPeriodeTexte: devis.modalites_periode_texte ?? '',
    modalitesLieu: devis.modalites_lieu ?? '',
    modalitesFormatSouhaite: devis.modalites_format_souhaite ?? '',
    modalitesContraintes: devis.modalites_contraintes ?? '',
    // Textes du devis
    resumeEchange: devis.resume_echange ?? '',
    conditionRemise: devis.condition_remise ?? '',
    // Offre exceptionnelle : une ligne libre sous les remises (migration 078)
    offreLibelle: devis.offre_libelle ?? '',
    offreHt: Number(devis.offre_ht) > 0 ? Number(devis.offre_ht) : '',
  };
}

function lignesVersFormulaire(lignes) {
  return lignes.map((l) => ({
    formationId: l.formation_id,
    // Session INTER retenue, et de quoi la nommer si elle n'est plus à venir.
    calendrierId: l.calendrier_id ?? null,
    sessionLibelle: l.session_date_debut
      ? libelleSession({
          date_debut: l.session_date_debut,
          date_fin: l.session_date_fin,
          module: l.session_module,
          lieux: l.session_intitule,
          places_restantes: l.session_places_restantes,
        })
      : null,
    libelle: l.libelle,
    mention: l.mention,
    objectif: l.objectif,
    groupeLibelle: l.groupe_libelle,
    format: l.format,
    lieu: l.lieu,
    unite: l.unite,
    dureeJours: Number(l.duree_jours),
    nbParticipants: Number(l.nb_participants),
    prixUnitaireHt: Number(l.prix_unitaire_ht),
    tauxRemise: Number(l.taux_remise),
    remiseJourneeHt: Number(l.remise_journee_ht) || 0,
    estOption: Boolean(l.est_option),
    optionRetenue: Boolean(l.option_retenue),
    motifRemise: l.motif_remise || '',
  }));
}

/**
 * Aperçu local, purement indicatif : le serveur reste seul maître des montants.
 * Renvoie null pour une ligne neuve, dont le tarif n'est pas encore connu.
 */
function apercuLigne(l) {
  if (l.prixUnitaireHt == null || l.unite == null) return null;

  const brut =
    l.unite === 'jour_participant'
      ? Number(l.prixUnitaireHt) * Number(l.dureeJours) * Number(l.nbParticipants)
      : Number(l.prixUnitaireHt) * Number(l.dureeJours);
  // Remise en % + remise par journée (par journée et par participant quand
  // la ligne est facturée par participant), jamais au-delà du brut.
  const joursFactures =
    l.unite === 'jour_participant'
      ? Number(l.dureeJours) * Number(l.nbParticipants)
      : Number(l.dureeJours);
  const remise = Math.min(
    (brut * (Number(l.tauxRemise) || 0)) / 100 + (Number(l.remiseJourneeHt) || 0) * joursFactures,
    brut
  );
  return { brut, remise, net: brut - remise };
}

/** Une option non retenue reste au devis, avec son prix, mais hors du total. */
const compteAuTotal = (l) => !l.estOption || Boolean(l.optionRetenue);

/**
 * Titre d'une section du formulaire de modification : un picto, le titre,
 * et à droite ce que la section alimente — le lecteur sait pourquoi il
 * remplit ces champs.
 */
function TitreEdition({ icone, titre, note }) {
  return (
    <div className="devis-edit-titre">
      <span className="devis-edit-titre-icone" aria-hidden="true">
        {icone}
      </span>
      <span>{titre}</span>
      {note && <span className="devis-edit-titre-note">{note}</span>}
    </div>
  );
}

function DevisPanel({ devis, onClose, onDevisChange, onValide, editionInitiale = false }) {
  // Ouvert depuis le crayon d'une liste : directement sur le formulaire,
  // sauf si le devis est validé (figé).
  const [edition, setEdition] = useState(Boolean(editionInitiale) && !devis.valide_le);
  const [entete, setEntete] = useState(() => etatDepuisDevis(devis));
  const [lignes, setLignes] = useState(() => lignesVersFormulaire(devis.lignes || []));
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [flash, setFlash] = useState(null);

  const [validation, setValidation] = useState(false);
  const [confirmerValidation, setConfirmerValidation] = useState(false);
  // Étape en cours de la confirmation : 'validation' puis 'envoi'. Sert à
  // garder l'attente sur le panneau, avec un libellé qui dit ce qui se passe.
  const [etapeValidation, setEtapeValidation] = useState(null);
  // Erreur de validation affichée dans le pied : le bandeau du haut est hors
  // de vue quand le devis est long, ce qui donnait un bouton « sans effet ».
  const [erreurValidation, setErreurValidation] = useState(null);

  // Catalogue, pour changer ou ajouter une formation en cours de modification.
  const [formations, setFormations] = useState([]);
  // Sessions INTER à venir, pour rattacher une ligne à une date du calendrier.
  const sessionsCalendrier = useSessionsCalendrier(edition);
  const [recherche, setRecherche] = useState('');

  useEffect(() => {
    if (!edition || formations.length) return;
    getFormations({ actif: true })
      .then((res) => setFormations(res.data || []))
      .catch(() => setFormations([]));
  }, [edition, formations.length]);

  /**
   * Ligne neuve : ni prix ni unité. Le serveur les résout depuis la grille
   * tarifaire à l'enregistrement — le navigateur ne fixe jamais un tarif.
   */
  function ajouterFormation(formationId) {
    const f = formations.find((x) => String(x.id) === String(formationId));
    if (!f) return;

    setLignes((prev) => [
      ...prev,
      {
        formationId: f.id,
        calendrierId: null,
        sessionLibelle: null,
        libelle: f.intitule,
        mention: null,
        objectif: null,
        groupeLibelle: '',
        format: 'inter_presentiel',
        lieu: null,
        unite: null,
        prixUnitaireHt: null,
        dureeJours: Number(f.dureeJours ?? f.duree_jours) || 1,
        nbParticipants: 1,
        tauxRemise: 0,
        motifRemise: '',
        nouvelle: true,
      },
    ]);
    setRecherche('');
  }

  /**
   * Changer la formation d'une ligne invalide son tarif : le prix dépend du
   * couple (formation, format). On remet unite/prix à null pour que le serveur
   * les reprenne dans la grille, plutôt que de conserver un tarif orphelin.
   */
  function changerFormation(index, formationId) {
    const f = formations.find((x) => String(x.id) === String(formationId));

    setLignes((prev) =>
      prev.map((l, i) => {
        if (i !== index) return l;
        if (!f) return { ...l, formationId: '' };
        return {
          ...l,
          formationId: f.id,
          // La session retenue portait l'ancienne formation : elle tombe.
          calendrierId: null,
          sessionLibelle: null,
          libelle: f.intitule,
          objectif: null,
          unite: null,
          prixUnitaireHt: null,
          dureeJours: Number(f.dureeJours ?? f.duree_jours) || l.dureeJours,
        };
      })
    );
  }

  /**
   * Même raisonnement : le format conditionne le tarif (groupe vs participant).
   * Et une ligne passée en INTRA n'a plus de session : sa date se négocie.
   */
  function changerFormat(index, format) {
    setLignes((prev) =>
      prev.map((l, i) =>
        i === index
          ? {
              ...l,
              format,
              unite: null,
              prixUnitaireHt: null,
              calendrierId: estInter(format) ? l.calendrierId : null,
              sessionLibelle: estInter(format) ? l.sessionLibelle : null,
            }
          : l
      )
    );
  }

  const resultatsRecherche = (() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return [];
    return formations
      .filter((f) => `${f.code} ${f.intitule}`.toLowerCase().includes(q))
      .slice(0, 8);
  })();

  const anomalies = devis.donnees_ia?.anomalies || [];
  const champsManquants = devis.champs_manquants || [];

  // Un devis validé est figé : plus de modification, le PDF fait foi.
  const verrouille = Boolean(devis.valide_le);
  // Devis figé mais jamais parti : l'envoi automatique qui suit la validation
  // a échoué (SMTP indisponible…). Sans reprise possible ici, le dossier
  // resterait bloqué — le devis ne pouvant plus être validé une seconde fois.
  const envoiEnAttente = verrouille && !devis.date_envoi;

  // Devis parti, mais le client n'a ni signé ni refusé : c'est le cas qui
  // justifie une relance. date_reponse_client couvre l'acceptation comme le
  // refus, statut couvre les devis clos autrement.
  const enAttenteReponse =
    Boolean(devis.date_envoi) &&
    !devis.date_reponse_client &&
    (devis.statut === 'envoye' || devis.statut === 'relance');

  // On compte à partir du dernier contact effectif : après une relance, ce
  // sont les jours depuis celle-ci qui disent si le client tarde, pas ceux
  // depuis l'envoi initial.
  const dernierContact = devis.date_derniere_relance || devis.date_envoi;
  const nbRelances = Number(devis.nb_relances) || 0;

  const [telechargement, setTelechargement] = useState(false);
  const [regeneration, setRegeneration] = useState(false);

  // Avant sa validation, le document est une estimation ; il devient le devis
  // une fois validé. Un devis établi hors de l'application (origine « externe »)
  // garde son fichier tel quel.
  const estimation = !devis.valide_le && ['brouillon', 'a_valider'].includes(devis.statut);
  const externe = devis.origine === 'externe';

  /** Refait le PDF depuis les données actuelles du devis, sans le télécharger. */
  async function lancerRegeneration() {
    setRegeneration(true);
    setErreur(null);
    setFlash(null);
    try {
      await regenererPdfDevis(devis.id);
      const frais = await getDevisById(devis.id);
      onDevisChange?.(frais.data);
      setFlash(estimation ? "L'estimation a été refaite." : 'Le PDF du devis a été refait.');
    } catch (err) {
      setErreur(err.message || 'Régénération impossible.');
    } finally {
      setRegeneration(false);
    }
  }

  /**
   * Le téléchargement produit le PDF côté serveur, ce qui renseigne pdf_path.
   * On relit ensuite le devis pour que le fichier apparaisse comme rattaché
   * à la demande, sans recharger la page.
   */
  async function lancerTelechargement() {
    setTelechargement(true);
    setErreur(null);
    setFlash(null);

    try {
      const nom = await telechargerPdfDevis(devis.id);
      const frais = await getDevisById(devis.id);
      onDevisChange?.(frais.data);
      setFlash(`PDF téléchargé (${nom}) et rattaché à la demande.`);
    } catch (err) {
      setErreur(err.message || 'Téléchargement impossible.');
    } finally {
      setTelechargement(false);
    }
  }

  // L'aperçu de l'email avant l'envoi : { estRelance, apresValidation } tant
  // que la modale est ouverte, null sinon. Rien ne part avant sa confirmation.
  const [apercuEnvoi, setApercuEnvoi] = useState(null);

  async function lancerValidation() {
    setValidation(true);
    setEtapeValidation('validation');
    setErreur(null);
    setErreurValidation(null);
    try {
      const res = await validerDevis(devis.id, { validePar: null });
      setConfirmerValidation(false);
      onDevisChange?.(res.data);
      // Plus d'envoi aveugle à la validation : l'email s'affiche d'abord,
      // modifiable, et ne part qu'au clic sur « Envoyer » dans l'aperçu.
      setApercuEnvoi({ apresValidation: true });
    } catch (err) {
      // On reste en confirmation : l'utilisateur voit le motif et peut réessayer.
      setErreurValidation(err.message || 'Validation impossible.');
    } finally {
      setValidation(false);
      setEtapeValidation(null);
    }
  }

  /**
   * Envoi du devis au client depuis le panneau, dans deux situations :
   * reprise d'un envoi initial qui a échoué, et relance d'un devis parti mais
   * resté sans réponse. Le serveur distingue les deux d'après le statut du
   * devis (cf. envoyerDevis, `estRelance`). Ici on n'ouvre que l'aperçu.
   */
  function relancerEnvoi({ estRelance = false } = {}) {
    setErreur(null);
    setApercuEnvoi({ estRelance });
  }

  /** L'envoi réel, confirmé depuis l'aperçu (texte éventuellement corrigé). */
  async function envoyerApresApercu({ sujet, texte }) {
    const { estRelance, apresValidation } = apercuEnvoi || {};
    setValidation(true);
    setEtapeValidation('envoi');
    try {
      const resEnvoi = await envoyerDevis(devis.id, { sujet, texte });
      const devisFinal = resEnvoi.data;
      const dest = devisFinal.client_email || devisFinal.contact_email;

      // Le devis est figé : la suite se joue sur la demande, pas sur le devis.
      // Le parent referme ce panneau et ouvre le détail de la demande.
      if (apresValidation && onValide) {
        onValide(devisFinal);
      } else {
        onDevisChange?.(devisFinal);
        setFlash(estRelance ? `Relance envoyée à ${dest}.` : `Devis envoyé par email à ${dest}.`);
      }
    } finally {
      setValidation(false);
      setEtapeValidation(null);
    }
  }

  /** Aperçu refermé sans envoyer : le devis validé reste à envoyer, et on le dit. */
  function fermerApercu() {
    const { apresValidation } = apercuEnvoi || {};
    setApercuEnvoi(null);
    if (apresValidation) {
      setFlash('Devis validé — email non envoyé. Le bouton « Envoyer au client » reste disponible.');
    }
  }

  // Un champ n'est « encore manquant » que s'il n'a pas été complété depuis.
  const champsAComplete = new Set(
    champsManquants
      .map((chemin) => CHEMIN_VERS_CHAMP[chemin])
      .filter((champ) => champ && !String(entete[champ] ?? '').trim())
  );

  const maj = (champ) => (e) => setEntete((prev) => ({ ...prev, [champ]: e.target.value }));

  const classeChamp = (champ) =>
    `devis-form-field${champsAComplete.has(champ) ? ' devis-field-manquant' : ''}`;

  function ouvrirEdition() {
    setEntete(etatDepuisDevis(devis));
    setLignes(lignesVersFormulaire(devis.lignes || []));
    setErreur(null);
    setFlash(null);
    setEdition(true);
  }

  function majLigne(index, champ, valeur) {
    setLignes((prev) => prev.map((l, i) => (i === index ? { ...l, [champ]: valeur } : l)));
  }

  function supprimerLigne(index) {
    setLignes((prev) => prev.filter((_, i) => i !== index));
  }

  /**
   * Une option de la formation de la ligne : même formation, même format, même
   * durée et même effectif, à ajuster (autre format, autre session, autre prix).
   * Le prix repart de la grille, comme une ligne neuve. Elle se range juste
   * après la ligne de base et ses options déjà posées.
   */
  function ajouterOption(index) {
    setLignes((prev) => {
      const base = prev[index];
      let fin = index;
      while (fin + 1 < prev.length && prev[fin + 1].estOption) fin += 1;
      const option = {
        ...base,
        calendrierId: null,
        sessionLibelle: null,
        unite: null,
        prixUnitaireHt: null,
        tauxRemise: 0,
        remiseJourneeHt: 0,
        motifRemise: '',
        estOption: true,
        optionRetenue: false,
        nouvelle: true,
      };
      return [...prev.slice(0, fin + 1), option, ...prev.slice(fin + 1)];
    });
  }

  /**
   * Les lignes lues par l'IA remplacent celles du formulaire. Une ligne déjà
   * présente pour la même formation (et de même nature, base ou option) garde
   * ce que le texte ne dit pas — session, mention, objectif — ; les autres
   * naissent comme une ligne ajoutée à la main. Plusieurs options pour une
   * même formation : une ligne chacune. Rien n'est enregistré avant « Enregistrer ».
   */
  function appliquerLignesIA(lues) {
    setLignes((prev) => {
      const utilisees = new Set();
      return lues.map((lue) => {
        const f = formations.find((x) => String(x.id) === String(lue.formationId));
        const k = prev.findIndex(
          (l, i) =>
            !utilisees.has(i) &&
            String(l.formationId) === String(lue.formationId) &&
            Boolean(l.estOption) === lue.estOption
        );
        if (k >= 0) utilisees.add(k);
        const base =
          k >= 0
            ? prev[k]
            : {
                nouvelle: true,
                calendrierId: null,
                sessionLibelle: null,
                mention: null,
                objectif: null,
                lieu: null,
                unite: null,
                prixUnitaireHt: null,
                motifRemise: '',
              };
        const format = lue.format || base.format || 'inter_presentiel';
        // Le tarif dépend du couple (formation, format) : un format changé sans prix
        // écrit rend le prix de la grille, pas l'ancien.
        const grille = lue.prixUnitaireHt === null && k >= 0 && format !== base.format;
        return {
          ...base,
          formationId: f ? f.id : lue.formationId,
          format,
          dureeJours: lue.dureeJours ?? base.dureeJours ?? (Number(f?.dureeJours ?? f?.duree_jours) || 1),
          nbParticipants: lue.nbParticipants ?? base.nbParticipants ?? 1,
          prixUnitaireHt: grille ? null : lue.prixUnitaireHt ?? base.prixUnitaireHt ?? null,
          unite: grille ? null : base.unite ?? null,
          tauxRemise: lue.tauxRemise ?? base.tauxRemise ?? 0,
          remiseJourneeHt: lue.remiseJourneeHt ?? base.remiseJourneeHt ?? 0,
          libelle: lue.libelle || base.libelle || f?.intitule || '',
          groupeLibelle: lue.groupeLibelle ?? base.groupeLibelle ?? '',
          estOption: lue.estOption,
          optionRetenue: lue.estOption && lue.optionRetenue,
        };
      });
    });
  }

  const nombreOuNull = (v) => (v === '' || v === null ? null : Number(v));
  const texteOuNull = (v) => (String(v ?? '').trim() === '' ? null : v);

  async function enregistrer(e) {
    e.preventDefault();
    setSaving(true);
    setErreur(null);

    try {
      const res = await updateDevis(devis.id, {
        client: {
          siret: texteOuNull(entete.siret),
          adresse: texteOuNull(entete.adresse),
          codePostal: texteOuNull(entete.codePostal),
          ville: texteOuNull(entete.ville),
          activite: texteOuNull(entete.activite),
          conventionCollective: texteOuNull(entete.conventionCollective),
        },
        cseEffectifEntreprise: nombreOuNull(entete.cseEffectifEntreprise),
        cseNbElus: nombreOuNull(entete.cseNbElus),
        cseTitulaires: nombreOuNull(entete.cseTitulaires),
        cseSuppleants: nombreOuNull(entete.cseSuppleants),
        cseNbPremierMandat: nombreOuNull(entete.cseNbPremierMandat),
        cseNbRenouvellement: nombreOuNull(entete.cseNbRenouvellement),
        cseNbDesignes: nombreOuNull(entete.cseNbDesignes),
        cseDernieresElections: texteOuNull(entete.cseDernieresElections),
        cseContexte: texteOuNull(entete.cseContexte),
        modalitesPeriodeTexte: texteOuNull(entete.modalitesPeriodeTexte),
        modalitesLieu: texteOuNull(entete.modalitesLieu),
        modalitesFormatSouhaite: texteOuNull(entete.modalitesFormatSouhaite),
        modalitesContraintes: texteOuNull(entete.modalitesContraintes),
        resumeEchange: texteOuNull(entete.resumeEchange),
        conditionRemise: texteOuNull(entete.conditionRemise),
        offreLibelle: texteOuNull(entete.offreLibelle),
        offreHt: entete.offreHt === '' || entete.offreHt == null ? 0 : Number(entete.offreHt),
        lignes: lignes.map((l) => ({
          ...l,
          dureeJours: Number(l.dureeJours),
          nbParticipants: Number(l.nbParticipants),
          calendrierId: l.calendrierId ? Number(l.calendrierId) : null,
          // null (et non 0) sur une ligne neuve laissée « selon la grille » :
          // c'est ce null qui déclenche la résolution du tarif côté serveur.
          // Un prix saisi, lui, part tel quel — c'est le prix de CE devis.
          prixUnitaireHt:
            l.prixUnitaireHt == null || String(l.prixUnitaireHt).trim() === ''
              ? null
              : Number(l.prixUnitaireHt),
          unite: l.unite ?? null,
          tauxRemise: Number(l.tauxRemise) || 0,
          remiseJourneeHt: Number(l.remiseJourneeHt) || 0,
          estOption: Boolean(l.estOption),
          optionRetenue: Boolean(l.estOption && l.optionRetenue),
        })),
      });
      onDevisChange?.(res.data);
      setEntete(etatDepuisDevis(res.data));
      setEdition(false);
      setFlash('Devis mis à jour, son PDF a été refait.');
    } catch (err) {
      setErreur(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  const totalApercu = Math.max(
    0,
    lignes
      .filter(compteAuTotal)
      .reduce((t, l) => t + (apercuLigne(l)?.net ?? 0), 0) - (Number(entete.offreHt) || 0)
  );
  const nbLignesDeBase = lignes.filter((l) => !l.estOption).length;
  const lignesSansTarif = lignes.filter((l) => !apercuLigne(l)).length;
  const resteAComplete = champsAComplete.size;

  return (
    <div className="devis-modal-overlay" onClick={onClose}>
      <div className="devis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">
              {devis.numero}
              {devis.origine === 'externe' && (
                <span className="badge-origine badge-origine-externe" title="Établi hors de l’application puis téléversé">
                  Externe
                </span>
              )}
            </div>
            <div className="devis-modal-sub">
              {devis.societe_nom || devis.client_societe} · émis le{' '}
              {formatDate(devis.date_emission)} · valable jusqu&apos;au{' '}
              {formatDate(devis.date_validite)}
            </div>
          </div>
          <div className="devis-modal-header-right">
            <span className="devis-status-pill devis-status-pill-large">
              {STATUT_DEVIS_LABELS[devis.statut] || devis.statut}
            </span>
            <button type="button" className="devis-drawer-close" onClick={onClose} aria-label="Fermer">
              ×
            </button>
          </div>
        </div>

        <div className="devis-modal-body">
          <FlashToast texte={flash} onFermer={() => setFlash(null)} />
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

          {verrouille && (
            <div className="devis-alert devis-alert-verrou">
              <strong>Devis validé le {formatDateTime(devis.valide_le)}</strong>
              {devis.valide_par ? ` par ${devis.valide_par}` : ''}. Il n&apos;est plus
              modifiable — le PDF attaché à la demande fait foi.
            </div>
          )}

          {!verrouille && (resteAComplete > 0 || anomalies.length > 0) && (
            <div className="devis-alert devis-alert-attention">
              <strong>À relire avant validation.</strong>
              {resteAComplete > 0 && (
                <div>
                  {resteAComplete} champ{resteAComplete > 1 ? 's' : ''} à compléter — ouvrez
                  «&nbsp;Modifier le devis&nbsp;», ils y sont surlignés.
                </div>
              )}
              {anomalies.map((a, i) => (
                <div key={i}>
                  {a.gravite === 'bloquant' ? '🔴' : '🟠'} {a.message}
                </div>
              ))}
            </div>
          )}

          {!edition && (
            <>
              <div className="devis-cartes">
                <div className="devis-carte">
                  <div className="devis-carte-titre">INFORMATIONS CLIENT</div>
                  <dl>
                    <dt>Société</dt>
                    <dd>{devis.societe_nom || devis.client_societe || '—'}</dd>
                    <dt>SIRET</dt>
                    <dd>{devis.siret || <span className="devis-manque">à compléter</span>}</dd>
                    <dt>Adresse</dt>
                    <dd>
                      {[devis.adresse, [devis.code_postal, devis.ville].filter(Boolean).join(' ')]
                        .filter(Boolean)
                        .join(', ') || <span className="devis-manque">à compléter</span>}
                    </dd>
                    <dt>Activité</dt>
                    <dd>{devis.activite || <span className="devis-manque">à compléter</span>}</dd>
                    <dt>Conv. collective</dt>
                    <dd>
                      {devis.convention_collective || (
                        <span className="devis-manque">à compléter</span>
                      )}
                    </dd>
                  </dl>
                </div>

                <div className="devis-carte">
                  <div className="devis-carte-titre">COMPOSITION DU CSE</div>
                  <dl>
                    <dt>Titulaires / suppléants</dt>
                    <dd>
                      {devis.cse_titulaires ?? '—'} / {devis.cse_suppleants ?? '—'}
                    </dd>
                    <dt>dont 1er mandat</dt>
                    <dd>{devis.cse_nb_premier_mandat ?? '—'}</dd>
                    <dt>dont renouvellement</dt>
                    <dd>{devis.cse_nb_renouvellement ?? '—'}</dd>
                    <dt>Dernières élections</dt>
                    <dd>
                      {devis.cse_dernieres_elections ? (
                        formatDate(devis.cse_dernieres_elections)
                      ) : (
                        <span className="devis-manque">à compléter</span>
                      )}
                    </dd>
                    <dt>Période souhaitée</dt>
                    <dd>
                      {devis.modalites_periode_texte || (
                        <span className="devis-manque">à compléter</span>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>

              {devis.resume_echange && (
                <>
                  <div className="devis-form-section-title">Résumé de l&apos;échange</div>
                  <div className="devis-resume">{devis.resume_echange}</div>
                </>
              )}

              {(devis.modalites || []).length > 0 && (
                <>
                  <div className="devis-form-section-title">Modalités de formation</div>
                  <div className="devis-modalites">
                    {devis.modalites.map((m) => (
                      <div key={m.id} className="devis-modalite-tuile">
                        <div className="devis-modalite-libelle">{m.libelle}</div>
                        <div className="devis-modalite-valeur">{m.valeur}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="devis-form-section-title">Détail des formations</div>
              {/* Sept colonnes : sur petit écran le tableau défile plutôt que
                  de comprimer les libellés. */}
              <div className="devis-table-scroll">
              <table className="devis-lignes-table">
                <thead>
                  <tr>
                    <th>PRESTATION</th>
                    <th>FORMAT</th>
                    <th>DURÉE</th>
                    <th>PART.</th>
                    <th>P.U. HT</th>
                    <th>REMISE</th>
                    <th className="devis-col-montant">TOTAL HT</th>
                  </tr>
                </thead>
                <tbody>
                  {(devis.lignes || []).map((l) => (
                    <tr key={l.id} className={l.est_option ? 'devis-ligne-option' : undefined}>
                      <td>
                        <div className="devis-ligne-libelle">
                          {l.est_option && (
                            <span className="devis-option-badge">
                              {l.option_retenue ? 'Option retenue' : 'En option'}
                            </span>
                          )}{' '}
                          {l.libelle}
                        </div>
                        {l.groupe_libelle && (
                          <div className="devis-ligne-sub">{l.groupe_libelle}</div>
                        )}
                      </td>
                      <td className="devis-nowrap">
                        {FORMATS.find((f) => f.value === l.format)?.label || l.format}
                        {l.session_date_debut && (
                          <div className="devis-ligne-sub">
                            Session{' '}
                            {String(l.session_date_debut) === String(l.session_date_fin)
                              ? `du ${formatDate(l.session_date_debut)}`
                              : `du ${formatDate(l.session_date_debut)} au ${formatDate(l.session_date_fin)}`}
                          </div>
                        )}
                        {!l.session_date_debut && estInter(l.format) && (
                          <div className="devis-ligne-sub">Date à définir</div>
                        )}
                      </td>
                      <td className="devis-nowrap">{Number(l.duree_jours)} j</td>
                      <td>{l.nb_participants}</td>
                      <td className="devis-nowrap">{formatMontant(l.prix_unitaire_ht)}</td>
                      <td className="devis-nowrap">
                        {Number(l.montant_remise_ht) > 0
                          ? `${[
                              Number(l.taux_remise) > 0 ? `−${Number(l.taux_remise)} %` : null,
                              Number(l.remise_journee_ht) > 0
                                ? `−${formatMontant(l.remise_journee_ht)}/j`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' et ')} (${formatMontant(l.montant_remise_ht)})`
                          : '—'}
                      </td>
                      <td className="devis-col-montant devis-nowrap">
                        {formatMontant(l.montant_ht)}
                        {l.est_option && !l.option_retenue && (
                          <div className="devis-ligne-sub">hors total</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <div className="devis-form-section-title">Récapitulatif financier</div>
              <dl className="devis-totaux">
                <dt>Total brut HT</dt>
                <dd>{formatMontant(devis.montant_brut_ht)}</dd>
                <dt>Remises</dt>
                <dd>−{formatMontant(devis.total_remises_ht)}</dd>
                {Number(devis.offre_ht) > 0 && (
                  <>
                    <dt>{devis.offre_libelle || 'Offre exceptionnelle'}</dt>
                    <dd>−{formatMontant(devis.offre_ht)}</dd>
                  </>
                )}
                <dt>Total HT</dt>
                <dd>{formatMontant(devis.montant_ht)}</dd>
                <dt>TVA ({Number(devis.taux_tva)} %)</dt>
                <dd>{formatMontant(devis.montant_tva)}</dd>
                <dt className="devis-total-fort">Total TTC</dt>
                <dd className="devis-total-fort">{formatMontant(devis.montant_ttc)}</dd>
              </dl>

            </>
          )}

          {edition && (
            <form onSubmit={enregistrer} className="devis-edit-form">
              <RemplirParTexte
                contexte="devis d'une formation CSE : coordonnées du client, composition du CSE, modalités, textes du devis"
                champs={[
                  { cle: 'siret', libelle: 'SIRET' },
                  { cle: 'activite', libelle: 'Activité' },
                  { cle: 'adresse', libelle: 'Adresse' },
                  { cle: 'codePostal', libelle: 'Code postal' },
                  { cle: 'ville', libelle: 'Ville' },
                  { cle: 'conventionCollective', libelle: 'Convention collective' },
                  { cle: 'cseEffectifEntreprise', libelle: "Effectif de l'entreprise", type: 'nombre' },
                  { cle: 'cseNbElus', libelle: "Nombre d'élus", type: 'nombre' },
                  { cle: 'cseTitulaires', libelle: 'Titulaires', type: 'nombre' },
                  { cle: 'cseSuppleants', libelle: 'Suppléants', type: 'nombre' },
                  { cle: 'cseNbPremierMandat', libelle: 'Élus en premier mandat', type: 'nombre' },
                  { cle: 'cseNbRenouvellement', libelle: 'Élus en renouvellement', type: 'nombre' },
                  { cle: 'cseNbDesignes', libelle: 'Référents à désigner', type: 'nombre' },
                  { cle: 'cseDernieresElections', libelle: 'Dernières élections', type: 'date' },
                  { cle: 'cseContexte', libelle: 'Contexte du CSE' },
                  { cle: 'modalitesPeriodeTexte', libelle: 'Période souhaitée' },
                  { cle: 'modalitesLieu', libelle: 'Lieu' },
                  {
                    cle: 'modalitesFormatSouhaite',
                    libelle: 'Format souhaité',
                    type: 'choix',
                    options: FORMATS_SOUHAITES.map((f) => ({ valeur: f.value, libelle: f.label })),
                  },
                  { cle: 'modalitesContraintes', libelle: 'Contraintes' },
                  { cle: 'resumeEchange', libelle: "Résumé de l'échange" },
                  { cle: 'conditionRemise', libelle: 'Condition de remise' },
                  { cle: 'offreLibelle', libelle: 'Offre exceptionnelle (libellé, sous les remises)' },
                  { cle: 'offreHt', libelle: 'Montant de l’offre (€ HT)', type: 'nombre' },
                ]}
                onValeurs={(v) => setEntete((prev) => ({ ...prev, ...v }))}
                formations={formations.map((f) => ({ id: f.id, code: f.code, intitule: f.intitule }))}
                onLignes={appliquerLignesIA}
              />
              <TitreEdition
                icone={<IconeBatiment />}
                titre="Informations client"
                note="En-tête du devis et du contrat"
              />
              <div className="devis-form-row">
                <label className={classeChamp('siret')}>
                  <span>SIRET</span>
                  <input type="text" value={entete.siret} onChange={maj('siret')} />
                </label>
                <label className={classeChamp('activite')}>
                  <span>Activité</span>
                  <input type="text" value={entete.activite} onChange={maj('activite')} />
                </label>
              </div>
              <div className="devis-form-row">
                <label className={classeChamp('adresse')} style={{ flex: 2 }}>
                  <span>Adresse</span>
                  <input type="text" value={entete.adresse} onChange={maj('adresse')} />
                </label>
                <label className={classeChamp('codePostal')}>
                  <span>Code postal</span>
                  <input type="text" value={entete.codePostal} onChange={maj('codePostal')} />
                </label>
                <label className={classeChamp('ville')}>
                  <span>Ville</span>
                  <SelectVille
                    value={entete.ville}
                    onChange={maj('ville')}
                    onChoisir={({ codePostal }) =>
                      setEntete((prev) => ({ ...prev, codePostal: prev.codePostal || codePostal }))
                    }
                    placeholder="Tapez une ville de France"
                  />
                </label>
              </div>
              <label className={classeChamp('conventionCollective')}>
                <span>Convention collective</span>
                <input
                  type="text"
                  value={entete.conventionCollective}
                  onChange={maj('conventionCollective')}
                />
              </label>

              <TitreEdition
                icone={<IconeGroupe />}
                titre="Composition du CSE"
                note="Encadré du devis"
              />
              <div className="devis-form-row">
                <label className={classeChamp('cseEffectifEntreprise')}>
                  <span>Effectif entreprise</span>
                  <input
                    type="number"
                    min="0"
                    value={entete.cseEffectifEntreprise}
                    onChange={maj('cseEffectifEntreprise')}
                  />
                </label>
                <label className={classeChamp('cseNbElus')}>
                  <span>Nombre d’élus</span>
                  <input
                    type="number"
                    min="0"
                    value={entete.cseNbElus}
                    onChange={maj('cseNbElus')}
                  />
                </label>
                <label className={classeChamp('cseTitulaires')}>
                  <span>Titulaires</span>
                  <input
                    type="number"
                    min="0"
                    value={entete.cseTitulaires}
                    onChange={maj('cseTitulaires')}
                  />
                </label>
                <label className={classeChamp('cseSuppleants')}>
                  <span>Suppléants</span>
                  <input
                    type="number"
                    min="0"
                    value={entete.cseSuppleants}
                    onChange={maj('cseSuppleants')}
                  />
                </label>
              </div>
              <div className="devis-form-row">
                <label className={classeChamp('cseNbPremierMandat')}>
                  <span>Dont 1er mandat</span>
                  <input
                    type="number"
                    min="0"
                    value={entete.cseNbPremierMandat}
                    onChange={maj('cseNbPremierMandat')}
                  />
                </label>
                <label className={classeChamp('cseNbRenouvellement')}>
                  <span>Dont renouvellement</span>
                  <input
                    type="number"
                    min="0"
                    value={entete.cseNbRenouvellement}
                    onChange={maj('cseNbRenouvellement')}
                  />
                </label>
                <label className={classeChamp('cseNbDesignes')}>
                  <span>Référents à désigner</span>
                  <input
                    type="number"
                    min="0"
                    value={entete.cseNbDesignes}
                    onChange={maj('cseNbDesignes')}
                  />
                </label>
                <label className={classeChamp('cseDernieresElections')}>
                  <span>Dernières élections</span>
                  <input
                    type="date"
                    value={entete.cseDernieresElections}
                    onChange={maj('cseDernieresElections')}
                  />
                </label>
              </div>
              <label className={classeChamp('cseContexte')}>
                <span>Contexte</span>
                <input type="text" value={entete.cseContexte} onChange={maj('cseContexte')} />
              </label>

              <TitreEdition
                icone={<IconeCalendrier />}
                titre="Modalités de formation"
                note="Période, lieu, format"
              />
              <div className="devis-form-row">
                <label className={classeChamp('modalitesPeriodeTexte')}>
                  <span>Période souhaitée</span>
                  <input
                    type="text"
                    placeholder="Septembre – Octobre 2026"
                    value={entete.modalitesPeriodeTexte}
                    onChange={maj('modalitesPeriodeTexte')}
                  />
                </label>
                <label className={classeChamp('modalitesLieu')}>
                  <span>Lieu</span>
                  <input
                    type="text"
                    value={entete.modalitesLieu}
                    onChange={maj('modalitesLieu')}
                  />
                </label>
                <label className={classeChamp('modalitesFormatSouhaite')}>
                  <span>Format souhaité</span>
                  <SelectRecherche
                    value={entete.modalitesFormatSouhaite}
                    onChange={maj('modalitesFormatSouhaite')}
                  >
                    <option value="">—</option>
                    {FORMATS_SOUHAITES.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
              </div>
              <label className={classeChamp('modalitesContraintes')}>
                <span>Contraintes</span>
                <input
                  type="text"
                  value={entete.modalitesContraintes}
                  onChange={maj('modalitesContraintes')}
                />
              </label>

              <TitreEdition
                icone={<IconeTexte />}
                titre="Textes du devis"
                note="Relus avant envoi"
              />
              <label className="devis-form-field">
                <span>Résumé de l&apos;échange (affiché en tête du PDF)</span>
                <textarea
                  rows={5}
                  value={entete.resumeEchange}
                  onChange={maj('resumeEchange')}
                />
              </label>
              <label className="devis-form-field">
                <span>Conditions des remises (note sous le tableau)</span>
                <textarea
                  rows={3}
                  value={entete.conditionRemise}
                  onChange={maj('conditionRemise')}
                />
              </label>
              {/* Offre exceptionnelle : une ligne libre sous les remises, pour
                  une offre qui n'est ni dans la grille ni dans une promotion. */}
              <div className="devis-form-row">
                <label className="devis-form-field" style={{ flex: 2 }}>
                  <span>Offre exceptionnelle (libellé, sous les remises)</span>
                  <input
                    type="text"
                    placeholder="Offre de bienvenue, 2e journée offerte…"
                    value={entete.offreLibelle}
                    onChange={maj('offreLibelle')}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Montant de l&apos;offre (€ HT)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0"
                    value={entete.offreHt}
                    onChange={maj('offreHt')}
                  />
                </label>
              </div>

              <TitreEdition
                icone={<IconeListe />}
                titre="Lignes du devis"
                note={`${lignes.length} ligne${lignes.length > 1 ? 's' : ''}`}
              />
              {lignes.map((l, i) => {
                const ap = apercuLigne(l);
                return (
                  <div key={i} className={`devis-ligne-edit${l.estOption ? ' devis-ligne-option' : ''}`}>
                    <div className="devis-ligne-edit-top">
                      <span className="devis-ligne-numero" aria-label={`Ligne ${i + 1}`}>
                        {i + 1}
                      </span>
                      {l.estOption && (
                        <span className="devis-option-badge" title="Proposée en plus de la ligne de base">
                          Option
                        </span>
                      )}
                      <SelectRecherche
                        className="devis-ligne-edit-libelle"
                        value={l.formationId ?? ''}
                        onChange={(e) => changerFormation(i, e.target.value)}
                        required
                      >
                        <option value="">— Choisir une formation —</option>
                        {formations.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.code} — {f.intitule}
                          </option>
                        ))}
                      </SelectRecherche>
                    </div>

                    <div className="devis-form-row">
                      <label className="devis-form-field">
                        <span>Format</span>
                        <SelectRecherche value={l.format} onChange={(e) => changerFormat(i, e.target.value)}>
                          {FORMATS.map((f) => (
                            <option key={f.value} value={f.value}>
                              {f.label}
                            </option>
                          ))}
                        </SelectRecherche>
                      </label>
                      <SelectSessionCalendrier
                        sessions={sessionsCalendrier}
                        formationId={l.formationId}
                        format={l.format}
                        value={l.calendrierId}
                        libelleValeur={l.sessionLibelle}
                        onChange={(e) => majLigne(i, 'calendrierId', e.target.value || null)}
                      />
                      <label className="devis-form-field">
                        <span>Durée (j)</span>
                        <input
                          type="number"
                          step="0.5"
                          min="0.5"
                          value={l.dureeJours}
                          onChange={(e) => majLigne(i, 'dureeJours', e.target.value)}
                          required
                        />
                      </label>
                      <label className="devis-form-field">
                        <span>Participants</span>
                        <input
                          type="number"
                          min="1"
                          value={l.nbParticipants}
                          onChange={(e) => majLigne(i, 'nbParticipants', e.target.value)}
                          required
                        />
                      </label>
                      <label className="devis-form-field">
                        <span>P.U. HT (€)</span>
                        {/* Toujours saisissable : le prix de ce devis, sans
                            toucher au catalogue. Vide = repris de la grille. */}
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="selon la grille"
                          value={l.prixUnitaireHt == null ? '' : l.prixUnitaireHt}
                          onChange={(e) =>
                            majLigne(i, 'prixUnitaireHt', e.target.value === '' ? null : e.target.value)
                          }
                          title={
                            l.prixUnitaireHt == null
                              ? 'Vide : tarif de la grille. Un prix saisi ne vaut que pour ce devis.'
                              : 'Prix de ce devis : le catalogue n’est pas modifié'
                          }
                        />
                      </label>
                      <label className="devis-form-field">
                        <span>Remise (%)</span>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="100"
                          value={l.tauxRemise}
                          onChange={(e) => majLigne(i, 'tauxRemise', e.target.value)}
                        />
                      </label>
                      <label className="devis-form-field">
                        <span>Remise par journée (€)</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0"
                          value={l.remiseJourneeHt || ''}
                          onChange={(e) => majLigne(i, 'remiseJourneeHt', e.target.value)}
                          title="Montant retiré à chaque journée facturée (par participant quand la ligne est facturée par participant)."
                        />
                      </label>
                    </div>

                    <div className="devis-form-row">
                      <label className="devis-form-field">
                        <span>
                          {l.estOption ? 'Libellé de l’option affiché sur le devis' : 'Libellé affiché sur le devis'}
                        </span>
                        <input
                          type="text"
                          value={l.libelle}
                          onChange={(e) => majLigne(i, 'libelle', e.target.value)}
                          required
                        />
                      </label>
                      {(Number(l.tauxRemise) > 0 || Number(l.remiseJourneeHt) > 0) && (
                        <label className="devis-form-field">
                          <span>Motif de la remise</span>
                          <input
                            type="text"
                            placeholder="Remise commerciale"
                            value={l.motifRemise}
                            onChange={(e) => majLigne(i, 'motifRemise', e.target.value)}
                          />
                        </label>
                      )}
                      {l.estOption && (
                        <label className="devis-form-field devis-option-retenue">
                          <span>Option retenue</span>
                          <input
                            type="checkbox"
                            checked={Boolean(l.optionRetenue)}
                            onChange={(e) => majLigne(i, 'optionRetenue', e.target.checked)}
                          />
                          <small>
                            {l.optionRetenue
                              ? 'Incluse au total du devis.'
                              : 'Affichée « en option » : hors total tant qu’elle n’est pas retenue.'}
                          </small>
                        </label>
                      )}
                    </div>

                    {/* Pied de la ligne : le calcul à gauche, la corbeille
                        seule à droite — loin du choix de la formation, pour
                        ne pas retirer une ligne en voulant la changer. */}
                    <div className="devis-ligne-edit-pied">
                      <div className="devis-ligne-apercu">
                        {ap ? (
                          <>
                            {l.unite === 'jour_participant'
                              ? `${l.prixUnitaireHt} € × ${l.dureeJours} j × ${l.nbParticipants} part.`
                              : `${l.prixUnitaireHt} € × ${l.dureeJours} j (forfait groupe)`}
                            {' = '}
                            {formatMontant(ap.brut)}
                            {ap.remise > 0 &&
                              ` − ${formatMontant(ap.remise)} = ${formatMontant(ap.net)}`}
                            {!compteAuTotal(l) && ' · hors total (option)'}
                          </>
                        ) : (
                          <span className="devis-ligne-neuve">
                            Nouvelle ligne · tarif appliqué depuis la grille à l&apos;enregistrement
                          </span>
                        )}
                      </div>
                      {!l.estOption && (
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          onClick={() => ajouterOption(i)}
                          title="Proposer une autre formule de cette formation, en option"
                        >
                          + Ajouter une option
                        </button>
                      )}
                      <button
                        type="button"
                        className="devis-icone-btn devis-icone-btn-danger"
                        onClick={() => supprimerLigne(i)}
                        disabled={!l.estOption && nbLignesDeBase <= 1}
                        aria-label="Retirer cette ligne"
                        title={
                          !l.estOption && nbLignesDeBase <= 1
                            ? 'Un devis doit garder au moins une formation hors option'
                            : 'Retirer cette ligne'
                        }
                      >
                        <IconeCorbeille />
                      </button>
                    </div>
                  </div>
                );
              })}

              <div className="devis-ajout-ligne">
                <label className="devis-form-field">
                  <span className="devis-ajout-titre">
                    <IconePlus />
                    Ajouter une formation
                  </span>
                  <input
                    type="search"
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    placeholder="Rechercher par code ou intitulé — SSCT, harcèlement, trésorier..."
                    autoComplete="off"
                  />
                </label>

                {recherche.trim() && (
                  <ul className="devis-recherche-resultats">
                    {resultatsRecherche.length === 0 && (
                      <li className="devis-recherche-vide">
                        Aucune formation ne correspond à «&nbsp;{recherche.trim()}&nbsp;».
                      </li>
                    )}
                    {resultatsRecherche.map((f) => (
                      <li key={f.id}>
                        <button type="button" onClick={() => ajouterFormation(f.id)}>
                          <span className="devis-recherche-code">{f.code}</span>
                          <span className="devis-recherche-intitule">{f.intitule}</span>
                          <span className="devis-recherche-duree">
                            {Number(f.dureeJours ?? f.duree_jours)} j
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="devis-apercu-total">
                Total HT indicatif : <strong>{formatMontant(totalApercu)}</strong>
                <span className="devis-apercu-note">
                  {lignesSansTarif > 0
                    ? `${lignesSansTarif} ligne${lignesSansTarif > 1 ? 's' : ''} sans tarif encore
                       chiffré${lignesSansTarif > 1 ? 'es' : ''} — total réel calculé à l'enregistrement.`
                    : "Montants définitifs recalculés à l’enregistrement."}
                </span>
              </div>

              {/* Collé au bas de la zone qui défile : le formulaire est long,
                  et les deux boutons ne doivent pas se mériter. */}
              <div className="devis-modal-actions devis-edit-actions">
                <button
                  type="button"
                  className="devis-btn"
                  onClick={() => setEdition(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="devis-btn devis-btn-primary devis-btn-avec-icone"
                  disabled={saving}
                >
                  <IconeCoche />
                  {saving ? 'Enregistrement' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          )}
        </div>

        {!edition && (
          <div className="devis-modal-footer">
            <button
              type="button"
              className="devis-btn"
              onClick={lancerTelechargement}
              disabled={telechargement || validation}
            >
              {telechargement ? 'Génération du PDF' : estimation ? 'Télécharger l’estimation' : 'Télécharger le PDF'}
            </button>
            <button
              type="button"
              className="devis-btn"
              onClick={lancerRegeneration}
              disabled={regeneration || telechargement || validation || externe}
              title={
                externe
                  ? 'Devis établi hors de l’application : son fichier fait foi, il ne se régénère pas'
                  : 'Refaire le PDF à partir des données actuelles du devis'
              }
            >
              {regeneration ? 'Régénération' : 'Régénérer le PDF'}
            </button>

            {envoiEnAttente && (
              <>
                <span className="devis-confirm-texte">
                  Devis validé, mais non parti : le client ne l'a pas reçu.
                </span>
                <button
                  type="button"
                  className="devis-btn devis-btn-primary"
                  onClick={() => relancerEnvoi()}
                  disabled={validation}
                >
                  {validation ? "Envoi de l'email..." : 'Envoyer au client'}
                </button>
              </>
            )}

            {enAttenteReponse && (
              <>
                <span className="devis-confirm-texte">
                  {nbRelances > 0
                    ? `Relancé ${formatAnciennete(dernierContact)} ` +
                      `(${nbRelances} relance${nbRelances > 1 ? 's' : ''}), ` +
                      'toujours sans réponse du client.'
                    : `Envoyé ${formatAnciennete(dernierContact)}, ` +
                      'toujours sans réponse du client.'}
                </span>
                <button
                  type="button"
                  className="devis-btn devis-btn-primary"
                  onClick={() => relancerEnvoi({ estRelance: true })}
                  disabled={validation}
                >
                  {validation ? 'Envoi de la relance' : 'Relancer le client'}
                </button>
              </>
            )}

            {!verrouille && !confirmerValidation && (
              <>
                <button type="button" className="devis-btn" onClick={ouvrirEdition}>
                  Modifier le devis
                </button>
                <button
                  type="button"
                  className="devis-btn devis-btn-primary"
                  onClick={() => setConfirmerValidation(true)}
                >
                  Valider le devis
                </button>
              </>
            )}

            {!verrouille && confirmerValidation && (
              <>
                <span
                  className={`devis-confirm-texte${
                    erreurValidation ? ' devis-confirm-texte-erreur' : ''
                  }`}
                >
                  {erreurValidation ||
                    (etapeValidation === 'validation'
                      ? 'Validation du devis et génération du PDF en cours'
                      : etapeValidation === 'envoi'
                        ? "Devis validé. Envoi de l'email au client en cours..."
                        : 'La validation fige le devis : plus aucune modification ne sera possible.')}
                </span>
                <button
                  type="button"
                  className="devis-btn"
                  onClick={() => {
                    setConfirmerValidation(false);
                    setErreurValidation(null);
                  }}
                  disabled={validation}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="devis-btn devis-btn-primary"
                  onClick={lancerValidation}
                  disabled={validation}
                >
                  {etapeValidation === 'envoi'
                    ? "Envoi de l'email..."
                    : validation
                      ? 'Validation'
                      : 'Confirmer la validation'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {apercuEnvoi && (
        <ApercuEmail
          titre={`Envoyer le devis ${devis.numero}`}
          sousTitre={devis.client_societe || devis.societe_nom}
          charger={() => apercuEnvoiDevis(devis.id)}
          onEnvoyer={envoyerApresApercu}
          onClose={fermerApercu}
        />
      )}
    </div>
  );
}

export default DevisPanel;

